import { CURSOR_MARKER, decodeKittyPrintable } from "@earendil-works/pi-tui";

interface TuiRenderTarget {
  requestRender(): void;
}

interface KeyMatcher {
  matches(data: string, action: string): boolean;
}

interface SecretInputTheme {
  fg(color: "accent" | "dim" | "muted" | "text", text: string): string;
}

/** A deliberately tiny password editor: no history, undo stack, kill ring, or plaintext render. */
export class MaskedSecretInput {
  focused = false;
  readonly #tui: TuiRenderTarget;
  readonly #theme: SecretInputTheme;
  readonly #keybindings: KeyMatcher;
  readonly #done: (value: string | undefined) => void;
  readonly #providerName: string;
  #value = "";
  #paste = "";
  #pasting = false;
  #settled = false;
  #error: string | undefined;

  constructor(
    tui: TuiRenderTarget,
    theme: SecretInputTheme,
    keybindings: KeyMatcher,
    done: (value: string | undefined) => void,
    providerName = "SiliconFlow",
  ) {
    this.#tui = tui;
    this.#theme = theme;
    this.#keybindings = keybindings;
    this.#done = done;
    this.#providerName = providerName;
  }

  render(width = 80): string[] {
    const outerWidth = Math.max(12, Math.min(66, width));
    const innerWidth = outerWidth - 2;
    const inputWidth = innerWidth - 2;
    const maskWidth = inputWidth - 1;
    const overflow = this.#value.length > maskWidth;
    const masked = overflow
      ? `…${"•".repeat(Math.max(0, maskWidth - 1))}`
      : "•".repeat(this.#value.length);
    const padding = " ".repeat(Math.max(0, inputWidth - masked.length - 1));
    const cursor = `${this.focused ? CURSOR_MARKER : ""}\u001b[7m \u001b[27m`;
    const border = "─".repeat(innerWidth);
    return [
      this.#theme.fg("accent", "Key"),
      "",
      this.#theme.fg("dim", `${this.#providerName} API Key`),
      "",
      this.#theme.fg("accent", `┌${border}┐`),
      `${this.#theme.fg("accent", "│")} ${this.#theme.fg("text", masked)}${cursor}${padding} ${this.#theme.fg("accent", "│")}`,
      this.#theme.fg("accent", `└${border}┘`),
      ...(this.#error === undefined ? [] : ["", this.#theme.fg("accent", this.#error)]),
      "",
      this.#theme.fg("muted", "Space / Enter Confirm   Esc Cancel"),
    ];
  }

  invalidate(): void {}

  handleInput(data: string): void {
    if (this.#settled) return;
    if (data.includes("\u001b[200~")) {
      this.#pasting = true;
      this.#paste = "";
      data = data.replace("\u001b[200~", "");
    }
    if (this.#pasting) {
      this.#paste += data;
      const end = this.#paste.indexOf("\u001b[201~");
      if (end === -1) return;
      this.#append(this.#paste.slice(0, end));
      const remaining = this.#paste.slice(end + 6);
      this.#paste = "";
      this.#pasting = false;
      if (remaining !== "") this.handleInput(remaining);
      this.#tui.requestRender();
      return;
    }
    if (this.#keybindings.matches(data, "tui.select.cancel")) {
      this.#settled = true;
      this.#value = "";
      this.#paste = "";
      this.#error = undefined;
      this.#done(undefined);
      return;
    }
    if (this.#keybindings.matches(data, "tui.input.submit") || data === "\n" || data === "\r") {
      this.#submit();
      return;
    }
    if (this.#keybindings.matches(data, "tui.editor.deleteCharBackward")) {
      this.#value = [...this.#value].slice(0, -1).join("");
      this.#tui.requestRender();
      return;
    }
    const printable = decodeKittyPrintable(data) ?? data;
    if (printable === " ") {
      this.#submit();
      return;
    }
    if (![...printable].some((character) => character.charCodeAt(0) < 32)) {
      this.#append(printable);
      this.#error = undefined;
      this.#tui.requestRender();
    }
  }

  dispose(): void {
    this.#value = "";
    this.#paste = "";
    this.#error = undefined;
  }

  #append(value: string): void {
    const remaining = Math.max(0, 16_384 - [...this.#value].length);
    this.#value += [...value]
      .filter((character) => {
        const code = character.charCodeAt(0);
        return code >= 32 && code !== 127 && (code < 128 || code > 159);
      })
      .slice(0, remaining)
      .join("");
  }

  #submit(): void {
    const submitted = this.#value.trim();
    if (submitted === "") {
      this.#error = "Enter an API Key before confirming.";
      this.#tui.requestRender();
      return;
    }
    this.#settled = true;
    this.#value = "";
    this.#paste = "";
    this.#error = undefined;
    this.#done(submitted);
  }
}
